import { useMemo } from 'react';
import { Calendar, Store, RefreshCw, Download, Loader2 } from 'lucide-react';

interface MonthStoreSelectorProps {
  dateFrom: string;
  dateTo: string;
  storeId: string | undefined;
  stores: Array<{ id: string; name: string }>;
  onDateChange: (dateFrom: string, dateTo: string, label: string) => void;
  onStoreChange: (storeId: string | undefined) => void;
  onRefresh: () => void;
  onDownloadSnapshot: () => void;
  isLoading: boolean;
  isDownloading?: boolean;
}

function generateMonthOptions() {
  const options: Array<{ label: string; dateFrom: string; dateTo: string }> = [];
  const now = new Date();
  const startYear = 2026;

  for (let y = now.getFullYear(); y >= startYear; y--) {
    const maxMonth = y === now.getFullYear() ? now.getMonth() + 1 : 12;
    for (let m = maxMonth; m >= 1; m--) {
      const dateFrom = `${y}-${String(m).padStart(2, '0')}-01`;
      const lastDay = new Date(y, m, 0).getDate();
      const dateTo = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      const label = new Date(y, m - 1).toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });
      options.push({ label, dateFrom, dateTo });
    }
  }
  return options;
}

export function MonthStoreSelector({
  dateFrom,
  dateTo,
  storeId,
  stores,
  onDateChange,
  onStoreChange,
  onRefresh,
  onDownloadSnapshot,
  isLoading,
  isDownloading,
}: MonthStoreSelectorProps) {
  const months = useMemo(() => generateMonthOptions(), []);
  const selectedMonth = months.find(m => m.dateFrom === dateFrom && m.dateTo === dateTo);

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 text-text-secondary" />
        <select
          value={selectedMonth ? `${selectedMonth.dateFrom}|${selectedMonth.dateTo}` : ''}
          onChange={(e) => {
            const [df, dt] = e.target.value.split('|');
            const m = months.find(m => m.dateFrom === df && m.dateTo === dt);
            if (m) onDateChange(m.dateFrom, m.dateTo, m.label);
          }}
          className="bg-surface-hover border border-border rounded-lg px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:border-accent-primary/50"
        >
          {months.map(m => (
            <option key={m.dateFrom} value={`${m.dateFrom}|${m.dateTo}`}>{m.label}</option>
          ))}
        </select>
      </div>

      {stores.length > 1 && (
        <div className="flex items-center gap-2">
          <Store className="w-4 h-4 text-text-secondary" />
          <select
            value={storeId || ''}
            onChange={(e) => onStoreChange(e.target.value || undefined)}
            className="bg-surface-hover border border-border rounded-lg px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:border-accent-primary/50"
          >
            <option value="">Wszystkie sklepy</option>
            {stores.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      )}

      <button
        onClick={onRefresh}
        disabled={isLoading}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-hover border border-border text-sm text-text-secondary hover:border-accent-primary/30 hover:text-accent-primary transition-all disabled:opacity-50"
      >
        {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        Diagnostyka
      </button>

      <button
        onClick={onDownloadSnapshot}
        disabled={isLoading || isDownloading}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-hover border border-border text-sm text-text-secondary hover:border-accent-primary/30 hover:text-accent-primary transition-all disabled:opacity-50"
        title="Pobierz kopię stanu faktur (JSON)"
      >
        {isDownloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
        Pobierz kopię stanu
      </button>
    </div>
  );
}
